/**
 * ショットカード周辺に相手側コメントのバブルを「ランダム風」に配置する
 * 純粋レイアウト関数。
 *
 * 要件:
 *  - カードの左右バンドへ交互に散らし、片側へ偏らないようにする。
 *    カードサムネイルに重なってもよい。
 *  - 縦は上から古い順（呼び出し側の配列順）を保って並べる。
 *  - コメント id のハッシュから決定的に配置する（再レンダリングや
 *    データ再取得で踊らない）。
 *  - バブル同士は重ねない。指定された境界（bounds）内に収める。
 *
 * DOM 非依存の純粋関数なので vitest で直接検証できる。
 */

export interface CloudRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface CloudBubbleSpec {
  /** 配置の一意キー（コメント ID）。ハッシュ配置のシード。 */
  id: string;
  /** バブルの推定幅(px)。呼び出し側が本文長などから見積もる。 */
  width: number;
  /** バブルの推定高さ(px)。 */
  height: number;
}

export interface CloudBubblePlacement {
  id: string;
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface CommentCloudLayoutOptions {
  /** カードの左右へはみ出してよい量(px)。既定: カード幅の 60%。 */
  reachX?: number;
  /** カード上端より上に置ける量(px)。既定: 16。 */
  overflowTop?: number;
  /** カード下端より下に置ける量(px)。既定: 16。 */
  overflowBottom?: number;
  /** バブル同士の最小間隔(px)。既定: 8。 */
  gap?: number;
}

// ===== 共有モーション定数 =====
// ShotCommentCloud / WaitingShotCloud で同じ質感のフェードインを共有する。

/** スタガーフェードインの間隔(s)。 */
export const CLOUD_STAGGER_SEC = 0.12;
/** 1 バブルのフェードイン所要時間(s)。 */
export const CLOUD_BUBBLE_FADE_SEC = 0.35;

// ===== 決定的擬似乱数 =====

/** FNV-1a 32bit 文字列ハッシュ。 */
export const hashString = (s: string): number => {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i += 1) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
};

/** mulberry32。シードから [0, 1) の決定的な乱数列を生成する。 */
export const mulberry32 = (seed: number): (() => number) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

// ===== 内部ヘルパ =====

const clamp = (v: number, min: number, max: number): number =>
  Math.min(max, Math.max(min, v));

const intersects = (
  a: CloudBubblePlacement,
  b: CloudBubblePlacement,
  gap: number,
): boolean =>
  a.left < b.left + b.width + gap &&
  b.left < a.left + a.width + gap &&
  a.top < b.top + b.height + gap &&
  b.top < a.top + a.height + gap;

// ===== 本体 =====

/**
 * バブル群の配置を計算する。
 *
 * 手順:
 *  1. 縦: カード上端の少し上 〜 カード下端の少し下 を配列順のスロットに等分割し、
 *     各バブルをスロット内へ（ハッシュ由来のジッター付きで）置く。
 *     → 上から下への順序が保たれる。
 *  2. 横: 左バンド / 右バンドを交互に割り当てる（ハッシュ任せだと同じ側に
 *     偏ることがあるため、交互で構造的にバランスさせる）。開始サイドは先頭
 *     バブル id のハッシュで決め、ショットごとの見た目の揺らぎを残す。
 *     バンド内位置は各バブル id のハッシュで決める。カードへの重なりは許容。
 *  3. 重なり解消: 先に置いたバブルと交差する場合は下方向へ押し出す。
 *  4. bounds 内へクランプ。
 */
export function layoutCommentBubbles(
  bubbles: CloudBubbleSpec[],
  cardRect: CloudRect,
  bounds: CloudRect,
  options?: CommentCloudLayoutOptions,
): CloudBubblePlacement[] {
  const n = bubbles.length;
  if (n === 0) return [];

  const reachX = options?.reachX ?? cardRect.width * 0.6;
  const overflowTop = options?.overflowTop ?? 16;
  const overflowBottom = options?.overflowBottom ?? 16;
  const gap = options?.gap ?? 8;

  const boundsRight = bounds.left + bounds.width;
  const boundsBottom = bounds.top + bounds.height;
  const cardRight = cardRect.left + cardRect.width;
  const cardBottom = cardRect.top + cardRect.height;

  // 縦スロット（配列順 = 上から下）。
  const spanTop = Math.max(bounds.top, cardRect.top - overflowTop);
  const spanBottom = Math.min(boundsBottom, cardBottom + overflowBottom);
  const spanHeight = Math.max(1, spanBottom - spanTop);
  const slotHeight = spanHeight / n;

  // 左右交互の開始サイド（0=左, 1=右）。
  const startSide = hashString(bubbles[0].id) & 1;

  const placements: CloudBubblePlacement[] = [];

  bubbles.forEach((bubble, i) => {
    const rng = mulberry32(hashString(bubble.id));
    const rX = rng();
    const rY = rng();

    // ── 横: 左右バンドの交互割り当て ──
    // バンドはカードのサムネイルへの被りを許す幅を持つ。
    let xMin: number;
    let xMax: number;
    if ((startSide + i) % 2 === 0) {
      // 左バンド: カード左端の外側〜カード左端に少し被る位置
      xMin = cardRect.left - reachX;
      xMax = cardRect.left - bubble.width * 0.3;
    } else {
      // 右バンド
      xMin = cardRight - bubble.width * 0.7;
      xMax = cardRight + reachX - bubble.width;
    }
    if (xMax < xMin) xMax = xMin;
    let left = xMin + rX * (xMax - xMin);
    left = clamp(left, bounds.left, Math.max(bounds.left, boundsRight - bubble.width));

    // ── 縦: スロット内配置（順序保持） ──
    const slotTop = spanTop + i * slotHeight;
    const jitterRange = Math.max(0, slotHeight - bubble.height);
    const top = slotTop + rY * jitterRange;

    const placement: CloudBubblePlacement = {
      id: bubble.id,
      left,
      top,
      width: bubble.width,
      height: bubble.height,
    };

    // ── 重なり解消: 先行バブルと交差したら、その下端の直下へ押し出す ──
    // 縦スロット順に処理しているので、下方向への押し出しで順序は崩れない。
    let guard = 0;
    let collided = placements.find((p) => intersects(placement, p, gap));
    while (collided && guard < n + 2) {
      placement.top = collided.top + collided.height + gap;
      collided = placements.find((p) => intersects(placement, p, gap));
      guard += 1;
    }

    // ── bounds 内へクランプ（下端あふれは押し戻す。極端に狭い場合のみ重なり許容） ──
    placement.top = clamp(
      placement.top,
      bounds.top,
      Math.max(bounds.top, boundsBottom - bubble.height),
    );

    placements.push(placement);
  });

  return placements;
}

export default layoutCommentBubbles;
